<script setup>
import { computed, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const isLoading = ref(true);
const isSaving = ref(false);
const isDisconnecting = ref(false);
const isAnsweringEnabled = ref(false);
const selectedAssistantId = ref('');
const showReplaceConfirm = ref(false);

const assistants = useMapGetter('captainAssistants/getRecords');

const inboxId = computed(() => Number(props.inbox?.id || route.params.inboxId));

// An inbox belongs to at most one assistant, so ownership is read straight off
// the assistant list rather than needing a lookup per assistant.
const connectedAssistant = computed(() =>
  assistants.value.find(assistant =>
    (assistant.inbox_ids || []).includes(inboxId.value)
  )
);

const assistantOptions = computed(() =>
  assistants.value.map(assistant => ({
    value: assistant.id,
    label: assistant.name,
  }))
);

const statusText = computed(() => {
  const assistant = connectedAssistant.value;
  if (!assistant) return t('CAPTAIN.INBOX_SETTINGS.STATUS.NOT_CONNECTED');

  return isAnsweringEnabled.value
    ? t('CAPTAIN.INBOX_SETTINGS.STATUS.CONNECTED', {
        assistant: assistant.name,
      })
    : t('CAPTAIN.INBOX_SETTINGS.STATUS.PAUSED', { assistant: assistant.name });
});

const isSubmitDisabled = computed(
  () =>
    !selectedAssistantId.value ||
    selectedAssistantId.value === connectedAssistant.value?.id
);

const replaceDescription = computed(() =>
  t('CAPTAIN.INBOX_SETTINGS.REPLACE.DESCRIPTION', {
    current: connectedAssistant.value?.name,
    next: assistants.value.find(
      assistant => assistant.id === selectedAssistantId.value
    )?.name,
  })
);

// The connection state lives on the join row, so it is read from the owning
// assistant's inbox list rather than from the inbox payload itself.
const loadState = async () => {
  isLoading.value = true;
  try {
    await store.dispatch('captainAssistants/get');
    const assistant = connectedAssistant.value;
    selectedAssistantId.value = assistant?.id ?? '';
    isAnsweringEnabled.value = false;

    if (assistant) {
      const payload = await store.dispatch('captainInboxes/get', {
        assistantId: assistant.id,
      });
      isAnsweringEnabled.value = Boolean(
        payload.find(record => record.id === inboxId.value)?.captain_enabled
      );
    }
  } finally {
    isLoading.value = false;
  }
};

const connect = async assistantId => {
  const { payload } = await store.dispatch('captainInboxes/create', {
    assistantId,
    inboxIds: [inboxId.value],
  });
  useAlert(
    payload.length
      ? t('CAPTAIN.INBOXES.TOGGLE.CONNECTED_MESSAGE')
      : t('CAPTAIN.INBOXES.CREATE.NONE_CONNECTED_MESSAGE')
  );
};

const disconnect = async assistantId => {
  await store.dispatch('captainInboxes/delete', {
    assistantId,
    inboxId: inboxId.value,
  });
};

const handleConnect = async () => {
  isSaving.value = true;
  try {
    await connect(selectedAssistantId.value);
  } catch (error) {
    useAlert(t('CAPTAIN.INBOXES.CREATE.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
    await loadState();
  }
};

const handleSubmit = () => {
  // Reassigning takes the inbox away from the assistant that has it, so it is
  // never a side effect of picking a different name in the dropdown.
  if (connectedAssistant.value) {
    showReplaceConfirm.value = true;
    return;
  }
  handleConnect();
};

const handleReplace = async () => {
  showReplaceConfirm.value = false;
  isSaving.value = true;
  try {
    await disconnect(connectedAssistant.value.id);
    await connect(selectedAssistantId.value);
  } catch (error) {
    useAlert(t('CAPTAIN.INBOXES.CREATE.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
    await loadState();
  }
};

const handleDisconnect = async () => {
  isDisconnecting.value = true;
  try {
    await disconnect(connectedAssistant.value.id);
    useAlert(t('CAPTAIN.INBOXES.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CAPTAIN.INBOXES.DELETE.ERROR_MESSAGE'));
  } finally {
    isDisconnecting.value = false;
    await loadState();
  }
};

// Pausing is deliberately not disconnecting: the join row survives, so the
// inbox keeps its assistant and only stops being answered.
const handleToggle = async enabled => {
  isAnsweringEnabled.value = enabled;
  try {
    await store.dispatch('captainInboxes/update', {
      assistantId: connectedAssistant.value.id,
      inboxId: inboxId.value,
      enabled,
    });
    useAlert(
      enabled
        ? t('CAPTAIN.INBOXES.TOGGLE.ENABLED_MESSAGE')
        : t('CAPTAIN.INBOXES.TOGGLE.DISABLED_MESSAGE')
    );
  } catch (error) {
    isAnsweringEnabled.value = !enabled;
    useAlert(t('CAPTAIN.INBOXES.TOGGLE.ERROR_MESSAGE'));
  }
};

const isAnswering = computed({
  get: () => isAnsweringEnabled.value,
  set: value => handleToggle(value),
});

const goToCreateAssistant = () => {
  router.push({
    name: 'captain_assistants_new',
    params: { accountId: route.params.accountId },
  });
};

watch(inboxId, loadState, { immediate: true });
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <LoadingState v-if="isLoading" />
    <div
      v-else-if="!assistants.length"
      class="flex flex-col items-start gap-2 py-4"
    >
      <p class="mb-0 text-heading-3 text-n-slate-12">
        {{ $t('CAPTAIN.INBOX_SETTINGS.EMPTY_STATE.TITLE') }}
      </p>
      <p class="mb-2 text-body-main text-n-slate-11">
        {{ $t('CAPTAIN.INBOX_SETTINGS.EMPTY_STATE.DESCRIPTION') }}
      </p>
      <NextButton
        :label="$t('CAPTAIN.INBOX_SETTINGS.EMPTY_STATE.ACTION')"
        @click="goToCreateAssistant"
      />
    </div>
    <template v-else>
      <form @submit.prevent="handleSubmit">
        <SettingsFieldSection
          :label="$t('CAPTAIN.INBOX_SETTINGS.TITLE')"
          :help-text="$t('CAPTAIN.INBOX_SETTINGS.DESC')"
          class="[&>div]:!items-start"
        >
          <SelectInput
            v-model="selectedAssistantId"
            :placeholder="$t('CAPTAIN.INBOX_SETTINGS.SELECT_PLACEHOLDER')"
            :options="assistantOptions"
          />
          <template #extra>
            <div class="grid grid-cols-1 lg:grid-cols-8 mt-3">
              <div class="col-span-1 lg:col-span-2 invisible" />
              <div class="col-span-1 lg:col-span-6 flex flex-col gap-3 mx-1">
                <p class="mb-0 text-label-small text-n-slate-11">
                  {{ statusText }}
                </p>
                <div class="flex gap-2">
                  <NextButton
                    type="submit"
                    :label="$t('CAPTAIN.INBOX_SETTINGS.SUBMIT')"
                    :is-loading="isSaving"
                    :disabled="isSubmitDisabled"
                  />
                  <NextButton
                    v-if="connectedAssistant"
                    type="button"
                    :is-loading="isDisconnecting"
                    faded
                    ruby
                    @click="handleDisconnect"
                  >
                    {{ $t('CAPTAIN.INBOX_SETTINGS.DISCONNECT') }}
                  </NextButton>
                </div>
              </div>
            </div>
          </template>
        </SettingsFieldSection>
      </form>
      <SettingsToggleSection
        v-if="connectedAssistant"
        v-model="isAnswering"
        class="mt-2"
        :header="$t('CAPTAIN.INBOX_SETTINGS.TOGGLE.HEADER')"
        :description="$t('CAPTAIN.INBOX_SETTINGS.TOGGLE.DESCRIPTION')"
      />
    </template>
    <ConfirmDialog
      v-if="showReplaceConfirm"
      v-model:show="showReplaceConfirm"
      :title="$t('CAPTAIN.INBOX_SETTINGS.REPLACE.TITLE')"
      :description="replaceDescription"
      :confirm-button-label="$t('CAPTAIN.INBOX_SETTINGS.REPLACE.CONFIRM')"
      :cancel-button-label="$t('CAPTAIN.INBOX_SETTINGS.REPLACE.CANCEL')"
      @confirm="handleReplace"
    />
  </div>
</template>
